import { useCallback, useEffect, useMemo, useState } from 'react'
import type { DealDossierTab } from '@/store/dealDossierStore'
import type { InspectorLogKind } from '@/pages/pipeline-intelligence/types'
import {
  InspectorLogActionRow,
  InspectorLogCompose,
} from '@/pages/pipeline-intelligence/InspectorLogActions'
import { buildWorkspaceModel } from './buildWorkspaceModel'
import { useEconomicsModel } from '../EconomicsPage'
import { PipelineStepper } from './PipelineStepper'
import { SourceLineageSidebar } from './SourceLineageSidebar'
import { FieldProvenancePanel } from './FieldProvenancePanel'
import { opensProvenanceNotDocument } from './sourceRoute'
import { formatFieldValue } from '../format'
import { NO_DEAL_EDIT, useCanEditDeal } from '../dealEditPermission'
import {
  DecisionRecordColumn,
  DecisionSnapshotColumn,
  RelationshipSidebar,
} from './OverviewColumns'
import type {
  DossierPipelineStage,
  DossierWorkspaceModel,
  KpiMetricId,
  WorkspaceBuildInput,
} from './types'

type Props = WorkspaceBuildInput & {
  /**
   * The model the host already built from the same inputs. Building it here
   * from `props` memoised nothing — `props` is a new object every render —
   * so every keystroke in the dossier rebuilt the whole workspace twice.
   */
  model?: DossierWorkspaceModel
  onGoto: (tab: DealDossierTab) => void
  onSaveNote?: (body: string) => void
  editDisabled?: boolean
  onOpenSource?: (fieldName: string | null) => void
  onStageInspect?: (stage: DossierPipelineStage) => void
  /** The linked property master's operating history, for the cash-flow summary. */
  operatingHistory?: Record<string, unknown>[] | null
}

export function DossierWorkspace(props: Props) {
  const { onGoto, onOpenSource, onStageInspect, loan } = props
  const { model: prebuilt, operatingHistory, ...input } = props
  const model = useMemo(() => prebuilt ?? buildWorkspaceModel(input), [prebuilt, input])
  const [selectedKpi, setSelectedKpi] = useState<KpiMetricId | null>(null)
  const [logKind, setLogKind] = useState<InspectorLogKind | null>(null)
  // The log dock below saves notes (pipeline:edit); its row is held shut for a
  // role without it, and the dock says why instead of inviting a pick.
  const canEditDeal = useCanEditDeal()
  // The figure whose derivation, basis or confirmation is open (a value no
  // document states never opens the document viewer).
  const [detailField, setDetailField] = useState<string | null>(null)

  useEffect(() => {
    setLogKind(null)
    setDetailField(null)
  }, [model.loanId])
  const detailRow = detailField
    ? [...model.loanOverview, ...model.propertyFields].find(
        (row) => (row.source?.fieldName ?? row.fieldName) === detailField,
      )
    : undefined
  const detailLineage = detailField ? props.lineage?.find((field) => field.fieldName === detailField) : undefined
  // A figure opened from outside the two tables (a KPI operand) is named and
  // valued from the catalogue and its lineage, not left as a bare field key.
  const detailDef = detailField && !detailRow ? props.defs?.find((def) => def.name === detailField) : undefined
  const lineageRows = props.lineage

  const showFieldDetail = useCallback((fieldName: string) => {
    setSelectedKpi(null)
    setDetailField(fieldName)
  }, [])
  // Every Overview request to open a figure's source comes through here, and
  // the lineage decides before the document viewer is asked: a Derived,
  // Suggested or Confirmed figure with no document opens where it comes from.
  // The viewer found no file for "derived: NOI / DSCR" and said "The
  // recorded source file is unavailable", as though a document had been lost.
  const openSource = useMemo(() => {
    if (!onOpenSource) return undefined
    return (fieldName: string | null) => {
      const row = fieldName ? lineageRows?.find((field) => field.fieldName === fieldName) : undefined
      if (fieldName && opensProvenanceNotDocument(row)) {
        showFieldDetail(fieldName)
        return
      }
      onOpenSource(fieldName)
    }
  }, [lineageRows, onOpenSource, showFieldDetail])

  const lineage = selectedKpi ? model.metricLineage(selectedKpi) : null
  const economics = useEconomicsModel({
    raw: props.raw,
    loanId: model.loanId,
    activity: props.activity,
    notes: props.notes,
    operatingHistory,
  })
  const logLoan = loan ?? { id: model.loanId, property: model.propertyName }

  return (
    <div className="flex h-full min-h-0 flex-col gap-1.5">
      <div className="shrink-0 rounded border border-stone-800/80 bg-[#0a0a0a] px-2 py-1.5">
        <PipelineStepper
          stages={model.pipelineStages}
          onStageClick={onStageInspect}
          totalAgeDays={model.totalAgeDays}
          targetClose={model.targetClose}
        />
      </div>

      <div className="flex min-h-0 flex-1 gap-1.5">
        {/* Snapshot and decision columns run the full height; only the right
            rail splits into sidebar + compose dock, so no filler cell sits
            empty under the left two columns. */}
        <div className="grid min-h-0 flex-1 grid-cols-12 gap-1.5 max-md:overflow-y-auto md:grid-rows-[minmax(0,1fr)_auto]">
          <div className="col-span-12 min-h-0 md:col-span-5 md:row-span-2">
            <DecisionSnapshotColumn
              loanOverview={model.loanOverview}
              propertyFields={model.propertyFields}
              parties={model.parties}
              raw={props.raw}
              loanId={model.loanId}
              economics={economics}
              cashFlowLineage={props.lineage}
              onSourceClick={openSource}
              onFieldDetail={showFieldDetail}
              onViewParties={() => onGoto('parties')}
              onViewCapitalStack={() => onGoto('capital_stack')}
            />
          </div>

          <div className="col-span-12 min-h-0 md:col-span-3 md:row-span-2">
            <DecisionRecordColumn
              decision={model.decision}
              conditions={model.conditions}
              underwritingChanges={model.underwritingChanges}
              risks={model.risks}
              strengths={model.strengths}
              journal={model.journal}
              onViewActivity={() => onGoto('audit')}
            />
          </div>

          <div className="col-span-12 min-h-0 md:col-span-4">
            <RelationshipSidebar
              loanId={model.loanId}
              onOpenParties={() => onGoto('parties')}
              nextActions={model.nextActions}
              communications={model.communications}
            />
          </div>

          {/* Lower-right dock — same Call/Email/Meeting/Note/Task compose as Deal Inspector */}
          <div className="col-span-12 md:col-span-4 md:col-start-9 md:row-start-2">
            <section className="overflow-hidden rounded border border-stone-800/80 bg-[#0a0a0a]">
              <header className="border-b border-stone-800/80 px-2.5 py-1.5">
                <h3 className="font-mono text-[9px] font-semibold uppercase tracking-[0.14em] text-amber-300/90">
                  Relationship & Activity
                </h3>
              </header>
              <div className="max-h-[min(46vh,420px)] overflow-y-auto p-2.5">
                {logKind ? (
                  <InspectorLogCompose
                    loan={logLoan}
                    kind={logKind}
                    onClose={() => setLogKind(null)}
                    onSaved={() => {
                      setLogKind(null)
                      onGoto('audit')
                    }}
                  />
                ) : (
                  <p className="px-1 py-3 text-center text-[11px] text-stone-500">
                    {canEditDeal
                      ? 'Choose Call, Email, Meeting, Note, or Task below to log to this deal — same flow as Deal Inspector.'
                      : NO_DEAL_EDIT}
                  </p>
                )}
              </div>
              <InspectorLogActionRow active={logKind} onSelect={setLogKind} />
            </section>
          </div>
        </div>

        {detailField && !lineage ? (
          <FieldProvenancePanel
            label={detailRow?.label ?? detailDef?.label ?? detailField.replace(/_/g, ' ')}
            value={detailRow?.value ?? formatFieldValue(detailLineage?.currentValue, detailDef?.kind, detailField)}
            field={detailLineage}
            onClose={() => setDetailField(null)}
            onOpenSource={openSource ? () => openSource(detailField) : undefined}
          />
        ) : null}

        {lineage ? (
          <SourceLineageSidebar
            lineage={lineage}
            onClose={() => setSelectedKpi(null)}
            onOpenSource={openSource}
            onFieldDetail={showFieldDetail}
          />
        ) : null}
      </div>
    </div>
  )
}

export { buildWorkspaceModel } from './buildWorkspaceModel'
export type { DossierWorkspaceModel, KpiMetricId } from './types'
